import {
  FormLabel,
  FormControl,
  FormErrorMessage,
  useTheme,
} from '@chakra-ui/react';
import { forwardRef, ForwardRefRenderFunction } from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';
import Select, {
  OptionTypeBase,
  Props as SelectProps,
  StylesConfig,
} from 'react-select';

export type SelectOption = {
  label: string;
  value: string;
};

interface ISelectProps extends SelectProps<OptionTypeBase> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  emptyOptionsMessage?: string;
  name: string;
  label?: string;
  error?: FieldError;
  isDisabled?: boolean;
}

const ReactSelectBase: ForwardRefRenderFunction<
  Select<OptionTypeBase, false>,
  ISelectProps
> = (
  {
    name,
    control,
    emptyOptionsMessage = 'Nenhuma opção disponível',
    label,
    options,
    placeholder = '',
    error,
    isDisabled = false,
    isRequired = false,
    ...rest
  },
  ref,
): JSX.Element => {
  const theme = useTheme();
  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined;

  const customStyles: StylesConfig<OptionTypeBase, false> = {
    menu: (provided) => ({
      ...provided,
      borderRadius: theme.radii.md,
      padding: 15,
      zIndex: 500,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 240,
      overflowY: 'auto',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1500,
    }),
    control: (provided, state) => {
      let shadow = provided.boxShadow;

      if (state.isFocused || state.menuIsOpen) {
        shadow = `0 0 0 1px ${theme.colors.blue[300]}`;
      } else if (error) {
        shadow = `0 0 0 1px ${theme.colors.red[500]}`;
      }

      let border = theme.colors.gray[300];

      if (state.isFocused || state.menuIsOpen) {
        border = `${theme.colors.blue[300]}`;
      } else if (error) {
        border = `${theme.colors.red[500]}`;
      }

      let opacity = 1;
      let cursor = 'auto';

      if (state.isDisabled) {
        opacity = 0.4;
        cursor = 'not-allowed';
      }

      return {
        ...provided,
        backgroundColor: theme.colors.gray[100],
        borderRadius: theme.radii.md,
        borderColor: border,
        boxShadow: shadow,
        cursor,
        fontSize: '18px',
        height: 48,
        opacity,
        padding: '0 4px',
        ':hover': {
          borderColor: border,
        },
        pointerEvents: 'auto',
      };
    },
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.colors.gray[700],
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    option: (provided, state: { isSelected: boolean }) => ({
      ...provided,
      borderRadius: theme.radii.md,
      color: state.isSelected ? 'white' : theme.colors.gray[700],
      height: 42,
      padding: '10px 20px',
      marginBottom: 4,
      cursor: 'pointer',
    }),
  };

  return (
    <FormControl
      isInvalid={!!error}
      isDisabled={isDisabled}
      isRequired={isRequired}
    >
      {!!label && <FormLabel htmlFor={name}>{label}</FormLabel>}

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <Select
            ref={ref}
            value={options?.find((option) => option.value === value)}
            onChange={(option) => onChange(option?.value)}
            onBlur={onBlur}
            options={options}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            styles={customStyles}
            placeholder={placeholder}
            noOptionsMessage={() => emptyOptionsMessage}
            isDisabled={isDisabled}
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            {...rest}
          />
        )}
      />

      {!!error && (
        <FormErrorMessage position="absolute">{error.message}</FormErrorMessage>
      )}
    </FormControl>
  );
};

export const ReactSelect = forwardRef(ReactSelectBase);
